import type { ReactNode } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { Text } from '@backstage/ui';
import { EphemeralGhostIcon } from '../ui/icons/ghostIcon';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { StatusChip } from '../ui';
import {
  branchActivity,
  branchKind,
  branchTier,
  type BranchActivity,
  type BranchKind,
  type BranchTier,
  type EnvironmentTiming,
} from './branchKind';
import { useAgeWords } from './elapsedWords';
import { projectEnvironmentsTranslationRef } from './translation';

/**
 * The sentence describing an ephemeral environment — « Environnement
 * éphémère, créé hier[, inactif depuis N j ?] », the age in `useAgeWords`'s
 * words — or undefined for a permanent one. The badge's tooltip and the
 * accessible name of the sidebar rows give this sentence.
 */
export function useBranchDescription(): (
  activity: BranchActivity,
) => string | undefined {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const ageWords = useAgeWords();
  return activity => {
    if (!activity.ephemeral) {
      return undefined;
    }
    if (activity.ageDays === undefined) {
      // No creation date, yet an idle time from the deployment history: the
      // badge says « inactive ? », so the sentence must say why.
      return activity.inactive
        ? t('branch.descriptionInactiveNoAge', {
            idle: String(activity.idleDays),
          })
        : t('branch.descriptionNoAge');
    }
    const age = ageWords(activity.ageDays);
    return activity.inactive
      ? t('branch.descriptionInactive', {
          age,
          idle: String(activity.idleDays),
        })
      : t('branch.description', { age });
  };
}

/**
 * The ephemeral badge alone: the ghost (ghostIcon.tsx) in the info pill, and
 * from `INACTIVE_AFTER_DAYS` idle days the ghost and « inactive ? » in the
 * warning pill. Nothing for a permanent branch.
 *
 * Never colour alone: the ghost has no words, so the pill is an image named
 * « Branche éphémère » (« Branche éphémère, inactive ? »), and the inactive
 * state keeps its visible words. The tooltip is a native `title`, not BUI's
 * Tooltip: the badge also sits inside interactive rows (the branch
 * switcher's menu items), where the `Focusable` a BUI Tooltip needs would
 * put a tab stop inside the item. It gives the name, then the age and idle
 * time (`useBranchDescription`) when they are known — on an `img`, a `title`
 * the name does not use is its accessible description as well.
 */
export function BranchKindBadge(props: { activity: BranchActivity }) {
  const { activity } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const describe = useBranchDescription();
  const description = describe(activity);

  if (!activity.ephemeral) {
    return null;
  }
  const label = activity.inactive
    ? t('branch.ephemeralInactive')
    : t('branch.ephemeral');
  // Without an age or an idle time, the sentence would only say
  // « Environnement éphémère » again.
  const hasDetail = activity.ageDays !== undefined || activity.inactive;
  return (
    <span
      role="img"
      aria-label={label}
      className="waadoo-pill waadoo-branch-badge"
      data-tone={activity.inactive ? 'warning' : 'info'}
      title={hasDetail ? `${label}\n${description}` : label}
    >
      <EphemeralGhostIcon />
      {activity.inactive ? <span>{t('branch.inactive')}</span> : null}
    </span>
  );
}

/**
 * « Suppression en cours » (`branchDeletion.ts`): an in-progress status chip
 * — the turning icon and the info tone of every « in progress » — in place of
 * the ephemeral badge, which it makes moot. The words are visible and read
 * as they are.
 */
export function BranchDeletingBadge() {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return (
    <Text variant="body-small" weight="bold">
      <StatusChip status="progressing" label={t('deletion.badge')} />
    </Text>
  );
}

const SleepingIcon = waadooIcons.sleeping;

/**
 * The tier in full (« Production », « Préproduction »…): the tooltip of an
 * environment tag and, where asked, the words after its name for assistive
 * technology.
 */
export const TIER_DESCRIPTION_KEYS = {
  production: 'tier.productionDescription',
  staging: 'tier.stagingDescription',
  development: 'tier.developmentDescription',
  ephemeral: 'tier.ephemeralDescription',
} as const satisfies Record<BranchTier, string>;

/**
 * The tier dot (`branchTier`): a disc in the tier's colour
 * (`--waadoo-tier-*`, styles.css), a dashed ring for an ephemeral branch.
 * The reduced form of the environment tag (`EnvironmentTag`), where a line
 * is too dense for it: the tasks page's small line, whose words are not
 * always the environment's name alone (« dev → preprod »). Decorative: never
 * the only mark of the tier — the words next to it name the environment.
 */
export function BranchTierDot(props: { branch: string }) {
  return (
    <span
      className="waadoo-tier-dot"
      data-tier={branchTier(props.branch)}
      aria-hidden="true"
    />
  );
}

/**
 * « En sommeil »: a permanent environment the project's appenv repository
 * declares and that is not deployed (`permanentEnvironments.ts`). The
 * neutral pill (`.waadoo-pill`, fg-secondary on neutral-2, covered by
 * components/ui/tokens.test.ts), the moon decorative — the words say it.
 */
export function SleepingBadge() {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return (
    <span className="waadoo-pill waadoo-sleeping-badge">
      <SleepingIcon aria-hidden focusable="false" />
      {t('sleeping.badge')}
    </span>
  );
}

/**
 * The kind icon of a branch, by its kind (`branchKind`): the shield of the
 * production, the fork of every other branch — an ephemeral one included,
 * told apart by its tier's violet (`EnvironmentTag`) and its ghost badge.
 */
export const BRANCH_KIND_ICONS: Record<BranchKind, RemixiconComponentType> = {
  production: waadooIcons.production,
  permanent: waadooIcons.branch,
  ephemeral: waadooIcons.branch,
};

export interface EnvironmentTagProps {
  branch: string;
  /**
   * The environment is asleep (`permanentEnvironments.ts`): the tag
   * outlined on the surface instead of tinted. The colour only repeats it —
   * the view says « en sommeil » in words or in the tag's `state`.
   */
  asleep?: boolean;
  /**
   * How the name renders — a link, where the name leads somewhere; the bare
   * branch name otherwise. The link is the name alone, so its accessible
   * name stays the branch's.
   */
  children?: ReactNode;
  /** Inside the tag, after the name: the promotion chain's state mark. */
  state?: ReactNode;
  /**
   * Hidden words after the name — the tier in full (`TIER_DESCRIPTION_KEYS`)
   * where the tier badge used to say it, so that a name computed from the
   * row (a table's row header, a heading) still says the tier.
   */
  tierWords?: string;
  /** The native tooltip; the tier in full, most of the time. */
  title?: string;
  /**
   * `icon`: the icon alone, without the name — the sidebar's compact row,
   * which writes the name itself (ellipsized) and says the kind in its
   * accessible name. Decorative then.
   */
  variant?: 'default' | 'icon';
}

/**
 * The environment tag (proposal A, 2026-10-29): ONE mark per environment in
 * place of the tier dot, the kind icon and the upper-case tier badge. Its
 * colour is the tier's (`branchTier`, `data-tier`: the production red, the
 * staging branches blue, dev grey, the ephemeral ones violet), its icon the
 * kind's (`BRANCH_KIND_ICONS`), tinted while the environment runs and
 * outlined while it sleeps (`data-asleep`) — the same code in the promotion
 * chain, the environments card, the branch page and every list
 * (`.waadoo-env-tag`, styles.css; contrast in components/ui/tokens.test.ts
 * « environment tag contrast »).
 *
 * No hook, so that the creation forms (scaffolder fields rendered without
 * the app's APIs in their tests) draw it too: the words it may need —
 * `tierWords`, `title` — come from the caller.
 */
export function EnvironmentTag(props: EnvironmentTagProps) {
  const tier = branchTier(props.branch);
  const KindIcon = BRANCH_KIND_ICONS[branchKind(props.branch)];
  const iconOnly = props.variant === 'icon';
  return (
    <span
      className="waadoo-env-tag"
      data-tier={tier}
      data-asleep={props.asleep ? '' : undefined}
      data-variant={iconOnly ? 'icon' : undefined}
      title={props.title}
      aria-hidden={iconOnly ? 'true' : undefined}
    >
      <KindIcon
        className="waadoo-env-tag__icon"
        aria-hidden
        focusable="false"
      />
      {iconOnly ? null : (
        <span className="waadoo-env-tag__name">
          {props.children ?? props.branch}
        </span>
      )}
      {!iconOnly && props.tierWords ? (
        <span className="waadoo-visually-hidden">, {props.tierWords}</span>
      ) : null}
      {iconOnly ? null : props.state}
    </span>
  );
}

export interface BranchLabelProps {
  branch: string;
  /** Where the age comes from. Without it, the badge says no age. */
  timing?: EnvironmentTiming;
  /** Epoch milliseconds the ages are counted to; the current time otherwise. */
  now?: number;
  /**
   * How the name itself renders — a link, most of the time. The bare branch
   * name otherwise. Kept apart from the badge, so a link's accessible name
   * stays the branch name and the badge is not part of what is clicked.
   */
  children?: ReactNode;
  /**
   * `sidebar`: the compact row of the left sidebar (ProjectsNav.tsx) —
   * one line, the name ellipsized, no ephemeral badge: the reduced
   * environment tag, its icon alone (`EnvironmentTag` `icon`), then the
   * name, which the row's own colours ink (`[data-variant='sidebar']`). The
   * sidebar tells an ephemeral branch by its group, its italic name and its
   * violet tag, and says « branche éphémère » in the row's accessible name.
   * The « Suppression en cours » chip is still shown, under the name.
   * `default` everywhere else.
   */
  variant?: 'default' | 'sidebar';
  /**
   * The environment is being deleted (`branchDeletion.ts`): the
   * « Suppression en cours » badge replaces the ephemeral one.
   */
  deleting?: boolean;
  /**
   * The environment is asleep (`permanentEnvironments.ts`): its tag
   * outlined, and the « En sommeil » pill after it unless `sleepingBadge`
   * is false; in the `sidebar` variant, the name muted instead of the pill
   * (the row says it in its accessible name).
   */
  sleeping?: boolean;
  /**
   * With `sleeping`, whether the « En sommeil » pill follows the tag:
   * false where another cell of the row says it already (the environments
   * card's « État » column). True by default.
   */
  sleepingBadge?: boolean;
  /**
   * After the badge, on the same wrapping line — the Odoo version badge
   * (`OdooVersionBadge`). Not shown by the `sidebar` variant.
   */
  aside?: ReactNode;
  /**
   * The tier in full after the name, for assistive technology only
   * (« prod, Production »): where the upper-case tier badge said it — the
   * environments card's rows and the branch page's header. Nothing for an
   * ephemeral branch, whose ghost badge says « Branche éphémère » (and the
   * name's `dev-` prefix when « Suppression en cours » replaces it). Not
   * shown by the `sidebar` variant.
   */
  tierInName?: boolean;
}

/**
 * A branch as every screen shows it: its environment tag (`EnvironmentTag`
 * — the tier's colour, the kind's icon, the name, tinted or outlined
 * asleep), then the ephemeral badge, « Suppression en cours » or
 * « En sommeil », then `aside`. One component on purpose — the sidebar, the
 * home page, the environments card, the branch page and its switcher all
 * render this, so the marker cannot diverge from one screen to the next.
 * Laid out by `.waadoo-branch-label` in styles.css; the sidebar's compact
 * row is its `sidebar` variant.
 */
export function BranchLabel(props: BranchLabelProps) {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const activity = branchActivity(
    props.branch,
    props.timing,
    props.now ?? Date.now(),
  );
  const kind = branchKind(props.branch);
  const tier = branchTier(props.branch);
  const sidebar = props.variant === 'sidebar';
  const name = props.children ?? props.branch;
  const tierDescription = t(TIER_DESCRIPTION_KEYS[tier]);

  let badge: ReactNode = null;
  if (props.deleting) {
    badge = <BranchDeletingBadge />;
  } else if (!sidebar && props.sleeping && props.sleepingBadge !== false) {
    // The hidden comma, as for the ephemeral badge below.
    badge = (
      <>
        <span className="waadoo-visually-hidden">,</span> <SleepingBadge />
      </>
    );
  } else if (!sidebar && activity.ephemeral) {
    // The comma, hidden on screen, separates the badge's name from the
    // branch's in a name computed from the whole row (a table's row header, a
    // menu item): each element's text is trimmed there, so the space has to
    // be a text node of its own — not rendered, being white space alone in
    // this flex container.
    badge = (
      <>
        <span className="waadoo-visually-hidden">,</span>{' '}
        <BranchKindBadge activity={activity} />
      </>
    );
  }

  return (
    <span
      className="waadoo-branch-label"
      data-variant={sidebar ? 'sidebar' : undefined}
      data-kind={kind}
      data-tier={tier}
      data-sleeping={props.sleeping ? 'true' : undefined}
    >
      {sidebar ? (
        <span className="waadoo-branch-label__name">
          {/* Decorative: the row's accessible name says the kind. */}
          <EnvironmentTag
            branch={props.branch}
            asleep={props.sleeping}
            variant="icon"
          />
          <span className="waadoo-branch-label__text">{name}</span>
        </span>
      ) : (
        <EnvironmentTag
          branch={props.branch}
          asleep={props.sleeping}
          title={tierDescription}
          tierWords={
            props.tierInName && tier !== 'ephemeral'
              ? tierDescription
              : undefined
          }
        >
          {name}
        </EnvironmentTag>
      )}
      {badge}
      {sidebar ? null : props.aside}
    </span>
  );
}
